import { useCallback } from 'react';

import { CombinedGraphQLErrors, gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  FAILED_ATTACHMENTS_EXTENSION_KEY,
  GraphQLInternalErrorCode,
} from '@this+that/constants';
import {
  CommentPresignedUrl,
  CreateCommentInput,
  DeleteCommentInput,
  FailedAttachmentInfo,
  GenerateCommentPresignedUrlInput,
  GuardDutyMalwareScanStatus,
  InboxEntryComment,
  PaginatedInboxEntryCommentsResponse,
  UpdateCommentInput,
} from '@this+that/models';
import { GraphQLFormattedError } from 'graphql';

export interface MutationResult<T> {
  data: T | null;
  errors?: readonly GraphQLFormattedError[];
}

const SCAN_STATUS_I18N_KEY_MAP: Record<string, string> = {
  NOT_FOUND: 'notFound',
  THREATS_FOUND: 'threatsFound',
  ACCESS_DENIED: 'accessDenied',
  UNSUPPORTED: 'unsupported',
};

export const mapScanStatusToI18nKey = (scanStatus: string): string =>
  SCAN_STATUS_I18N_KEY_MAP[scanStatus] ?? 'default';

export const extractFailedAttachments = (
  errors: readonly GraphQLFormattedError[],
): FailedAttachmentInfo[] | null => {
  const aggregated = errors.find((e) =>
    Array.isArray(e.extensions?.[FAILED_ATTACHMENTS_EXTENSION_KEY]),
  );
  if (!aggregated) {
    return null;
  }
  return aggregated.extensions![
    FAILED_ATTACHMENTS_EXTENSION_KEY
  ] as FailedAttachmentInfo[];
};

const NOT_FOUND_SCAN_I18N_KEY = 'notFound';

export function buildFailedAttachmentToastHandler(
  errorMessage: (msg: string) => void,
  warningMessage: (msg: string) => void,
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  return (info: FailedAttachmentInfo) => {
    const fileName = info.fileName ?? '';

    if (info.reason === GraphQLInternalErrorCode.UNSUPPORTED_FILE_CONTENT) {
      errorMessage(t('attachments.errors.fileContentMismatch', { fileName }));
      return;
    }

    if (info.reason === GraphQLInternalErrorCode.UNREADABLE_FILE_CONTENT) {
      errorMessage(t('attachments.errors.fileUnreadable', { fileName }));
      return;
    }

    if (info.scanStatus) {
      const i18nKey = mapScanStatusToI18nKey(
        info.scanStatus as GuardDutyMalwareScanStatus,
      );

      if (i18nKey === NOT_FOUND_SCAN_I18N_KEY) {
        warningMessage(t(`attachments.errors.${i18nKey}`, { fileName }));
      } else {
        errorMessage(t(`attachments.errors.${i18nKey}`, { fileName }));
      }

      return;
    }

    errorMessage(t('attachments.errors.default', { fileName }));
  };
}

const INBOX_ENTRY_COMMENT_FIELDS = gql`
  fragment InboxEntryCommentFields on InboxEntryComment {
    id
    inboxEntryId
    userId
    userName
    message
    createdAt
    updatedAt
    attachments {
      id
      fileName
      url
      disposition
    }
  }
`;

const GET_PAGINATED_COMMENTS_BY_INBOX_ENTRY = gql`
  ${INBOX_ENTRY_COMMENT_FIELDS}
  query getPaginatedCommentsByInboxEntry(
    $inboxEntryId: String!
    $limit: Int!
    $exclusiveStartKey: String
  ) {
    getPaginatedCommentsByInboxEntry(
      inboxEntryId: $inboxEntryId
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
    ) {
      comments {
        ...InboxEntryCommentFields
      }
      exclusiveStartKey
    }
  }
`;

interface GetPaginatedCommentsByInboxEntryData {
  getPaginatedCommentsByInboxEntry: PaginatedInboxEntryCommentsResponse;
}

interface GetPaginatedCommentsByInboxEntryParams {
  inboxEntryId: string;
  limit: number;
  exclusiveStartKey?: string | null;
}

export function useGetPaginatedCommentsByInboxEntry() {
  const { loading, refetch } = useQuery<GetPaginatedCommentsByInboxEntryData>(
    GET_PAGINATED_COMMENTS_BY_INBOX_ENTRY,
    { skip: true },
  );

  const getPaginatedCommentsByInboxEntry = useCallback(
    async ({
      inboxEntryId,
      limit,
      exclusiveStartKey,
    }: GetPaginatedCommentsByInboxEntryParams): Promise<PaginatedInboxEntryCommentsResponse> => {
      const { data } = await refetch({
        inboxEntryId,
        limit,
        exclusiveStartKey,
      });

      return (
        data?.getPaginatedCommentsByInboxEntry ?? {
          comments: [],
          exclusiveStartKey: null,
        }
      );
    },
    [refetch],
  );

  return { getPaginatedCommentsByInboxEntry, loading };
}

const GENERATE_COMMENT_PRESIGNED_URL = gql`
  mutation generateCommentPresignedUrl($input: GenerateCommentPresignedUrlInput!) {
    generateCommentPresignedUrl(input: $input) {
      url
      fields
      attachmentId
    }
  }
`;

interface GenerateCommentPresignedUrlData {
  generateCommentPresignedUrl: CommentPresignedUrl;
}

export function useGenerateCommentPresignedUrl() {
  const [generateCommentPresignedUrlMutation, { loading, error }] =
    useMutation<GenerateCommentPresignedUrlData>(
      GENERATE_COMMENT_PRESIGNED_URL,
    );

  const generateCommentPresignedUrl = useCallback(
    async (
      input: GenerateCommentPresignedUrlInput,
      signal?: AbortSignal,
    ): Promise<MutationResult<CommentPresignedUrl>> => {
      try {
        const { data } = await generateCommentPresignedUrlMutation({
          variables: { input },
          context: signal ? { fetchOptions: { signal } } : undefined,
        });

        return { data: data?.generateCommentPresignedUrl ?? null };
      } catch (error) {
        if (CombinedGraphQLErrors.is(error)) {
          return { data: null, errors: error.errors };
        }

        throw error;
      }
    },
    [generateCommentPresignedUrlMutation],
  );

  return { generateCommentPresignedUrl, loading, error };
}

const CREATE_COMMENT = gql`
  ${INBOX_ENTRY_COMMENT_FIELDS}
  mutation createComment($input: CreateCommentInput!) {
    createComment(input: $input) {
      ...InboxEntryCommentFields
    }
  }
`;

interface CreateCommentData {
  createComment: InboxEntryComment;
}

export function useCreateComment() {
  const [createCommentMutation, { loading, error }] =
    useMutation<CreateCommentData>(CREATE_COMMENT);

  const createComment = useCallback(
    async (
      input: CreateCommentInput,
    ): Promise<MutationResult<InboxEntryComment>> => {
      try {
        const { data } = await createCommentMutation({
          variables: { input },
        });

        return { data: data?.createComment ?? null };
      } catch (error) {
        if (CombinedGraphQLErrors.is(error)) {
          return { data: null, errors: error.errors };
        }

        throw error;
      }
    },
    [createCommentMutation],
  );

  return { createComment, loading, error };
}

const UPDATE_COMMENT = gql`
  ${INBOX_ENTRY_COMMENT_FIELDS}
  mutation updateComment($input: UpdateCommentInput!) {
    updateComment(input: $input) {
      ...InboxEntryCommentFields
    }
  }
`;

interface UpdateCommentData {
  updateComment: InboxEntryComment;
}

export function useUpdateComment() {
  const [updateCommentMutation, { loading, error }] =
    useMutation<UpdateCommentData>(UPDATE_COMMENT);

  const updateComment = useCallback(
    async (
      input: UpdateCommentInput,
    ): Promise<MutationResult<InboxEntryComment>> => {
      try {
        const { data } = await updateCommentMutation({
          variables: { input },
        });

        return { data: data?.updateComment ?? null };
      } catch (error) {
        if (CombinedGraphQLErrors.is(error)) {
          return { data: null, errors: error.errors };
        }

        throw error;
      }
    },
    [updateCommentMutation],
  );

  return { updateComment, loading, error };
}

const DELETE_COMMENT = gql`
  mutation deleteComment($input: DeleteCommentInput!) {
    deleteComment(input: $input)
  }
`;

interface DeleteCommentData {
  deleteComment: boolean;
}

export function useDeleteComment() {
  const [deleteCommentMutation, { loading, error }] =
    useMutation<DeleteCommentData>(DELETE_COMMENT);

  const deleteComment = useCallback(
    async (input: DeleteCommentInput) => {
      const { data } = await deleteCommentMutation({ variables: { input } });
      return data?.deleteComment ?? false;
    },
    [deleteCommentMutation],
  );

  return { deleteComment, loading, error };
}
